import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, LogOut, Settings } from 'lucide-react';
import { NAVIGATION, type NavFeature, type NavLeaf, type NavSection } from '@/navigation';
import { useAuth } from '@/auth/AuthProvider';
import { useAgency } from '@/auth/AgencyProvider';
import { usePermissions } from '@/auth/PermissionsProvider';
import { useProfile } from '@/data/useProfile';
import { useDriverPayModes } from '@/data/useDriverSettlements';
import { useReceivablesPending } from '@/data/useReceivablesPending';
import { useChatUnread } from '@/realtime/ChatUnread';
import { badgeText } from '@/lib/chat';
import { cn } from '@/lib/utils';
import { Wordmark } from './Wordmark';

/**
 * The rail: docs/estilo-visual.md §Layout.
 *
 * Fixed, dark, --size-rail-w wide, with the wordmark at the top, the menu in the middle and the user
 * card at the bottom. The active item is a SOLID BLUE PILL — not an underline, not a border.
 *
 * Dense (UX audit S1): 36 px rows (--size-nav-row-h), 32 px sub-items, no tagline under the wordmark, and a
 * section is open only while it holds the active route (the chevron still opens any of them). That keeps
 * Dashboard to Support on one 900 px screen. The drawer keeps the 44 px touch rows.
 */
/** 'rail' is the desktop column; 'drawer' is the same menu inside the phone drawer (MobileNav), where
 *  every icon button grows to the 44 px touch target. */
type SidebarVariant = 'rail' | 'drawer';
const VariantContext = React.createContext<SidebarVariant>('rail');

/** The square icon buttons of the rail: 32 px on the desktop, the 44 px touch target in the drawer. */
function iconButtonSize(variant: SidebarVariant) {
  return variant === 'drawer' ? 'size-touch' : 'size-8';
}

function itemClasses(active: boolean, nested: boolean, variant: SidebarVariant) {
  return cn(
    'flex items-center rounded-md px-3 transition-colors duration-(--duration-fast) ease-out',
    variant === 'drawer' ? 'h-touch text-base' : nested ? 'h-8 text-sm' : 'h-nav-row text-sm',
    // A nested entry sits under its group's label with a tighter indent and gap, so the longest label
    // ("Broker Payments") reads in full beside its count badge in the 232 px rail.
    nested ? 'gap-2 pl-8 pr-2.5' : 'gap-3',
    active
      ? 'bg-accent-base font-medium text-on-accent'
      : 'text-on-rail-muted hover:bg-rail-2 hover:text-on-rail',
  );
}

function LeafLink({ item, nested, rollup }: { item: NavLeaf; nested: boolean; rollup?: React.ReactNode }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const variant = React.useContext(VariantContext);
  return (
    <NavLink
      to={item.path}
      end={!item.coversSubpaths}
      className={({ isActive }) => itemClasses(isActive, nested, variant)}
    >
      <Icon className={nested || variant === 'rail' ? 'size-4' : 'size-5'} aria-hidden="true" />
      <span className="truncate">{t(`nav.${item.id}`)}</span>
      {item.id === 'chat' && <ChatBadge />}
      {item.id === 'brokersPayments' && <PaymentsBadgeGate />}
      {rollup}
    </NavLink>
  );
}

/** The route is the section's own page or one of the pages below it. */
function holdsRoute(section: NavSection, pathname: string) {
  const paths = [section.path, ...(section.children ?? []).map((c) => c.path)];
  return paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** The Payments count only for the roles that can mark a load invoiced: the read never runs for the others. */
function PaymentsBadgeGate() {
  const { can } = usePermissions();
  return can('receivable.mark_invoiced') ? <PaymentsBadge /> : null;
}

/**
 * audit-b-receivables (C5): the loads waiting to be invoiced to their broker on the Payments entry. Neutral (work
 * to do, not an alarm); hidden at zero; 1..99, then 99+.
 */
function PaymentsBadge() {
  const { t } = useTranslation();
  const pending = useReceivablesPending();
  const count = pending.status === 'ready' ? pending.data.length : 0;
  const text = badgeText(count);
  if (!text) return null;
  return (
    <span
      className="ml-auto min-w-5 rounded-full bg-rail-2 px-1.5 text-center text-xs font-semibold leading-5 text-on-rail"
      aria-label={t('nav.toInvoiceBadge', { count })}
      data-testid="payments-badge"
    >
      {text}
    </span>
  );
}

/** Unread conversations on the Chat entry (driver-office-chat, design 7.1): 1..99, then 99+. */
function ChatBadge() {
  const { t } = useTranslation();
  const chat = useChatUnread();
  const text = chat ? badgeText(chat.unreadThreads) : null;
  if (!text) return null;
  return (
    <span
      className="ml-auto min-w-5 rounded-full bg-danger px-1.5 text-center text-xs font-semibold leading-5 text-white"
      aria-label={t('chat.badge', { count: chat?.unreadThreads ?? 0 })}
      data-testid="chat-badge"
    >
      {text}
    </span>
  );
}

/** A leaf that shows only while its feature is in use. Mounted only after the leaf's gate passed, so the
 *  read behind it never runs for a role that cannot see the entry anyway. */
function FeatureLeaf({ item, nested }: { item: NavLeaf & { feature: NavFeature }; nested: boolean }) {
  const modes = useDriverPayModes();
  if (modes.status !== 'ready' || !modes.data.inUse) return null;
  return <LeafLink item={item} nested={nested} />;
}

function Leaf({ item, nested }: { item: NavLeaf; nested: boolean }) {
  return item.feature ? <FeatureLeaf item={{ ...item, feature: item.feature }} nested={nested} /> : <LeafLink item={item} nested={nested} />;
}

function Section({ section }: { section: NavSection }) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const children = section.children?.filter((c) => !c.gate || can(c.gate));
  const { pathname } = useLocation();
  const active = holdsRoute(section, pathname);
  // Closed unless it holds the active route; arriving on one of its pages opens it (a manual close stays
  // closed until the route changes again).
  const [open, setOpen] = React.useState(active);
  React.useEffect(() => {
    if (active) setOpen(true);
  }, [active, pathname]);
  const panelId = `nav-${section.id}`;
  const label = t(`nav.${section.id}`);
  const Chevron = open ? ChevronDown : ChevronRight;
  const variant = React.useContext(VariantContext);

  // A top-level entry honours its own gate too (activity-feed: Activity needs activity.read).
  if (section.gate && !can(section.gate)) return null;
  if (!children) return <LeafLink item={section} nested={false} />;

  return (
    <div>
      {/* When the section's own page is the active one, the pill is the WHOLE row, chevron included:
          painted on the link alone it stopped short of the chevron and read as a clipped pill. */}
      <div className="group/section flex items-center rounded-md has-[a[aria-current=page]]:bg-accent-base">
        <div className="min-w-0 flex-1">
          {/* A closed Brokers still shows the Payments count, on its own row: closing the group must not
              hide the work waiting in it. */}
          <LeafLink
            item={section}
            nested={false}
            rollup={!open && children.some((c) => c.id === 'brokersPayments') ? <PaymentsBadgeGate /> : undefined}
          />
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={t(open ? 'nav.collapse' : 'nav.expand', { section: label })}
          className={cn(
            'ml-1 grid shrink-0 place-items-center rounded-md text-on-rail-muted hover:bg-rail-2 hover:text-on-rail',
            'group-has-[a[aria-current=page]]/section:text-on-accent group-has-[a[aria-current=page]]/section:hover:bg-accent-hover',
            iconButtonSize(variant),
          )}
        >
          <Chevron className="size-4" aria-hidden="true" />
        </button>
      </div>
      {open && (
        <div id={panelId} className="mt-0.5 flex flex-col gap-0.5">
          {children.map((child) => (
            <Leaf key={child.id} item={child} nested />
          ))}
        </div>
      )}
    </div>
  );
}

function UserCard() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const agency = useAgency();
  const email = user?.email ?? '';
  // The profile name the team sees (design 5.3); the email stands in until there is one, and stays in
  // the title either way.
  const { profile } = useProfile();
  const shown = profile?.displayName || email;
  // The role of the ACTIVE membership in the current agency (AgencyProvider), the same one the server
  // honours. No membership says so rather than inventing a badge.
  const roleLabel =
    agency.status === 'ready'
      ? t(`roles.${agency.role}`)
      : agency.status === 'loading'
        ? t('user.roleLoading')
        : agency.status === 'none'
          ? t('user.roleNone')
          : agency.status === 'suspended'
            ? t('user.roleSuspended')
          : t('user.roleUnknown');
  const initial = shown.slice(0, 1).toUpperCase() || '?';
  const variant = React.useContext(VariantContext);

  return (
    <div className="flex shrink-0 items-center gap-3 border-t border-rail-2 px-3 py-3">
      <span
        className="grid size-9 shrink-0 place-items-center rounded-full bg-rail-2 text-base font-semibold text-on-rail"
        aria-hidden="true"
      >
        {initial}
      </span>
      <span className="min-w-0 flex-1">
        {/* The full name reads (up to two lines, never "Demo Ad..."); the title holds it with the email. */}
        <span
          title={shown === email ? email : `${shown}\n${email}`}
          data-testid="user-card-name"
          className="line-clamp-2 text-sm leading-tight font-medium [overflow-wrap:anywhere] text-on-rail"
        >
          {shown}
        </span>
        <span title={roleLabel} className="block truncate text-xs text-on-rail-muted">
          {roleLabel}
        </span>
      </span>
      <NavLink
        to="/settings"
        title={t('user.settings')}
        aria-label={t('user.settings')}
        className={({ isActive }) =>
          cn(
            'grid shrink-0 place-items-center rounded-md',
            iconButtonSize(variant),
            isActive ? 'bg-accent-base text-on-accent' : 'text-on-rail-muted hover:bg-rail-2 hover:text-on-rail',
          )
        }
      >
        <Settings className="size-5" aria-hidden="true" />
      </NavLink>
      <button
        type="button"
        onClick={() => void signOut()}
        title={t('user.signOut')}
        aria-label={t('user.signOut')}
        className={cn(
          'grid shrink-0 place-items-center rounded-md text-on-rail-muted hover:bg-rail-2 hover:text-on-rail',
          iconButtonSize(variant),
        )}
      >
        <LogOut className="size-5" aria-hidden="true" />
      </button>
    </div>
  );
}

export function Sidebar({ variant = 'rail' }: { variant?: SidebarVariant }) {
  const { t } = useTranslation();
  // The chat is not offered to accounting (driver-office-chat, Hector decision 2).
  const chat = useChatUnread();
  const agency = useAgency();
  const hideChat = agency.status === 'ready' && agency.role === 'accounting' && (chat === null || !chat.enabled);

  return (
    <VariantContext.Provider value={variant}>
    {/* Rail: sticky and h-screen, not fixed: the navy footer has to be able to run underneath it (see
        AppLayout). Drawer: the full height of the drawer panel, which is itself fixed. */}
    <aside
      className={cn(
        'flex w-full shrink-0 flex-col bg-rail',
        variant === 'rail' ? 'sticky top-0 z-(--z-sticky) h-screen w-rail' : 'h-full',
      )}
    >
      {/* No tagline under the lockup (UX audit S1): the footer already carries the brand line. */}
      <div className={cn('shrink-0 px-5 pt-4 pb-3', variant === 'drawer' && 'pr-14')}>
        <Wordmark tone="rail" size="md" />
      </div>

      {/* The scroller and the list are SEPARATE elements on purpose. With the items as direct flex
          children of a height-constrained column they shrink below --size-row-h instead of
          overflowing: a 44px row silently became 21px. The inner list sizes to its content and the
          nav scrolls around it. */}
      <nav aria-label={t('brand.product')} className="rail-scroll min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        <div className="flex flex-col gap-0.5">
          {NAVIGATION.filter((section) => !(hideChat && section.id === 'chat')).map((section) => (
            <Section key={section.id} section={section} />
          ))}
        </div>
      </nav>

      <UserCard />

      {/* The motto gives way on short screens (below 60rem tall, i.e. most laptops): there the menu needs
          the room more, and the footer already carries the brand line. */}
      <p className="shrink-0 px-5 pt-4 pb-5 text-lg leading-tight font-bold text-on-rail [@media(max-height:60rem)]:hidden">
        {t('brand.mottoLine1')}
        <br />
        <span className="text-accent-base">{t('brand.mottoLine2')}</span>
        <br />
        {t('brand.mottoLine3')}
      </p>
    </aside>
    </VariantContext.Provider>
  );
}
